import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScannedQRData, SelectedProduct } from '@/types/payment';
import { toaster } from '@/components/ui/toaster';
import { useParseQR } from './useParseQR';
import { usePay } from './usePay';

type DrawerType = 'scan' | 'myqr';
type PaymentStep = 'amount-input' | 'confirm' | 'success' | 'error';

interface UsePaymentDrawersResult {
  isScanDrawerOpen: boolean;
  isMyQRDrawerOpen: boolean;
  isPaymentDrawerOpen: boolean;
  currentStep: PaymentStep;
  scannedData: ScannedQRData | null;
  paymentAmount: number | null;
  transactionId: string | null;
  errorMessage: string | null;
  canGoBack: boolean;
  handleScanClick: () => void;
  handleMyQRClick: () => void;
  handleScanClose: () => void;
  handleQRScanned: (data: ScannedQRData) => void;
  handleQRScan: (result: string) => void;
  handleQRCodeFromUrl: (encodedValue: string) => Promise<void>;
  handleResetScanned: (resetFn: () => void) => void;
  handleAmountSubmit: (amount: number, selectedProducts: SelectedProduct[]) => void;
  handlePaymentConfirm: () => Promise<void>;
  handlePaymentCancel: () => void;
  handlePaymentDrawerClose: () => void;
  handleRetryPayment: () => void;
  handleGoBack: () => void;
  setIsScanDrawerOpen: (open: boolean) => void;
  setIsMyQRDrawerOpen: (open: boolean) => void;
}

/**
 * 支払いページのドロワー状態とルーティングを管理するカスタムHook
 */
export const usePaymentDrawers = (): UsePaymentDrawersResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const parseQRMutation = useParseQR();
  const payMutation = usePay();
  const [isScanDrawerOpen, setIsScanDrawerOpen] = useState(false);
  const [isMyQRDrawerOpen, setIsMyQRDrawerOpen] = useState(false);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<PaymentStep>('amount-input');
  const [scannedData, setScannedData] = useState<ScannedQRData | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<SelectedProduct[]>([]);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // QRスキャナーのリセット関数を保持
  const resetScannedRef = useRef<(() => void) | null>(null);

  // URL経由で渡されたQRコードを保持
  const qrEncodedValueRef = useRef<string | null>(null);

  // ルーティングからのドロワー開閉制御
  useEffect(() => {
    const state = location.state as { openDrawer?: DrawerType; qrEncodedValue?: string } | null;

    // URL経由でQRコードが渡された場合
    if (state?.qrEncodedValue) {
      qrEncodedValueRef.current = state.qrEncodedValue;
      // stateをクリアして再度処理されないようにする
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (state?.openDrawer === 'scan') {
      setIsScanDrawerOpen(true);
      // stateをクリアして再度開かないようにする
      navigate(location.pathname, { replace: true, state: {} });
    } else if (state?.openDrawer === 'myqr') {
      setIsMyQRDrawerOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // URL経由のQRコード処理
  useEffect(() => {
    const processQRFromUrl = async () => {
      const encodedValue = qrEncodedValueRef.current;
      if (!encodedValue) return;

      // 処理済みフラグをクリア
      qrEncodedValueRef.current = null;

      if (process.env.NODE_ENV === 'development') {
        console.log('URL経由でQRコードを処理:', encodedValue);
      }

      try {
        // APIでQRコードを解析
        const parsedData = await parseQRMutation.mutateAsync({
          encodedValue: encodedValue,
        });

        // APIレスポンスをScannedQRData形式に変換
        const scannedQRData: ScannedQRData = {
          type: parsedData.type,
          qrId: parsedData.qrId,
          timestamp: parsedData.timestamp,
          issuedAt: parsedData.issuedAt,
          payeeName: parsedData.payeeName,
          payeeDetail: parsedData.payeeDetail,
          amount: parsedData.amount,
          encodedValue: encodedValue, // エンコード値を保存
          // 互換性のために追加
          merchantId: parsedData.qrId,
          merchantName: parsedData.payeeDetail || parsedData.payeeName,
        };

        handleQRScanned(scannedQRData);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'QRコードの解析に失敗しました';

        toaster.create({
          title: 'エラー',
          description: errorMessage,
          type: 'error',
          duration: 5000,
        });
      }
    };

    processQRFromUrl();
  }, [location.state, parseQRMutation]);

  const handleScanClick = () => {
    setIsScanDrawerOpen(true);
    setScannedData(null);
    setPaymentAmount(null);
    setTransactionId(null);
  };

  const handleMyQRClick = () => {
    setIsMyQRDrawerOpen(true);
  };

  const handleScanClose = () => {
    setIsScanDrawerOpen(false);
    setIsPaymentDrawerOpen(false);
    setCurrentStep('amount-input');
    setScannedData(null);
    setPaymentAmount(null);
    setTransactionId(null);
    setErrorMessage(null);
  };

  // QRコードスキャン処理
  const handleQRScan = async (result: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('QRコードをスキャンしました:', result);
    }

    try {
      // URLからエンコード文字列を抽出
      let encodedValue = result;

      // URLの場合、末尾のエンコード文字列のみを抽出
      // 例: http://localhost:3000/main/payment/qr/U1RPUkU6... → U1RPUkU6...
      if (result.startsWith('http://') || result.startsWith('https://')) {
        const urlParts = result.split('/');
        encodedValue = urlParts[urlParts.length - 1];
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('エンコード値:', encodedValue);
      }

      // APIでQRコードを解析
      const parsedData = await parseQRMutation.mutateAsync({
        encodedValue: encodedValue,
      });

      // APIレスポンスをScannedQRData形式に変換
      const scannedQRData: ScannedQRData = {
        type: parsedData.type,
        qrId: parsedData.qrId,
        timestamp: parsedData.timestamp,
        issuedAt: parsedData.issuedAt,
        payeeName: parsedData.payeeName,
        payeeDetail: parsedData.payeeDetail,
        amount: parsedData.amount,
        encodedValue: encodedValue, // エンコード値を保存
        // 互換性のために追加
        merchantId: parsedData.qrId,
        merchantName: parsedData.payeeDetail || parsedData.payeeName,
      };

      handleQRScanned(scannedQRData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'QRコードの解析に失敗しました';

      toaster.create({
        title: 'エラー',
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });

      // エラー時にスキャン状態をリセット
      if (resetScannedRef.current) {
        setTimeout(() => {
          resetScannedRef.current?.();
        }, 3000);
      }
    }
  };

  // QRスキャナーのリセット関数を設定
  const handleResetScanned = (resetFn: () => void) => {
    resetScannedRef.current = resetFn;
  };

  // URL経由でQRコードを処理する関数
  const handleQRCodeFromUrl = async (encodedValue: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('URL経由でQRコードを処理:', encodedValue);
    }

    try {
      // APIでQRコードを解析
      const parsedData = await parseQRMutation.mutateAsync({
        encodedValue: encodedValue,
      });

      // APIレスポンスをScannedQRData形式に変換
      const scannedQRData: ScannedQRData = {
        type: parsedData.type,
        qrId: parsedData.qrId,
        timestamp: parsedData.timestamp,
        issuedAt: parsedData.issuedAt,
        payeeName: parsedData.payeeName,
        payeeDetail: parsedData.payeeDetail,
        amount: parsedData.amount,
        // 互換性のために追加
        merchantId: parsedData.qrId,
        merchantName: parsedData.payeeDetail || parsedData.payeeName,
      };

      handleQRScanned(scannedQRData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'QRコードの解析に失敗しました';

      toaster.create({
        title: 'エラー',
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });

      // エラー時はホームに戻る
      navigate('/main/payments', { replace: true });
    }
  };

  // QRコードスキャン成功時の処理
  const handleQRScanned = (data: ScannedQRData) => {
    setScannedData(data);
    setIsScanDrawerOpen(false);

    // ボトムシートのアニメーション完了を待ってから支払いドロワーを開く
    setTimeout(() => {
      // パターンA: 金額情報がある場合は確認画面へ
      if (data.amount !== null && data.amount !== undefined) {
        setPaymentAmount(data.amount);
        setCurrentStep('confirm');
      } else {
        // パターンB: 金額情報がない場合は入力画面へ
        setCurrentStep('amount-input');
      }
      setIsPaymentDrawerOpen(true);
    }, 300);
  };

  // 金額入力完了時の処理
  const handleAmountSubmit = (amount: number, selectedProducts: SelectedProduct[]) => {
    setPaymentAmount(amount);
    setSelectedItems(selectedProducts);
    setCurrentStep('confirm');
  };

  // 支払い確定時の処理
  const handlePaymentConfirm = async () => {
    if (!scannedData) {
      toaster.create({
        title: 'エラー',
        description: 'QRコード情報が見つかりません',
        type: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      // エンコード値を取得
      const encodedValue = scannedData.encodedValue;

      if (!encodedValue) {
        toaster.create({
          title: 'エラー',
          description: 'QRコードのエンコード値が見つかりません',
          type: 'error',
          duration: 3000,
        });
        return;
      }

      // 支払いリクエストを構築
      const paymentRequest: {
        encodedValue: string;
        amount?: number;
        items?: { productCode: string; quantity: number }[];
      } = {
        encodedValue: encodedValue,
      };

      if (selectedItems.length > 0) {
        // 商品選択時：items を送信し、amount は送信しない
        paymentRequest.items = selectedItems.map(sp => ({
          productCode: sp.product.id,
          quantity: sp.quantity,
        }));
      } else if (scannedData.amount == null) {
        // 手入力金額の場合：amount を送信
        if (!paymentAmount) {
          toaster.create({
            title: 'エラー',
            description: '支払い金額が指定されていません',
            type: 'error',
            duration: 3000,
          });
          return;
        }
        paymentRequest.amount = paymentAmount;
      }

      // 支払いAPIを呼び出し
      const result = await payMutation.mutateAsync(paymentRequest);

      // トランザクションコードを設定
      setTransactionId(result.transactionCode);

      if (process.env.NODE_ENV === 'development') {
        console.log('支払い成功:', result);
      }

      toaster.create({
        title: '支払いが完了しました',
        description: `฿${result.amount.toLocaleString()} を支払いました`,
        type: 'success',
        duration: 3000,
      });

      // アニメーション完了を待ってから画面遷移（1.5秒）
      await new Promise(resolve => setTimeout(resolve, 1500));

      // 完了画面へ遷移
      setCurrentStep('success');
    } catch (error) {
      console.error('支払い処理エラー:', error);
      const errorMessage =
        error instanceof Error ? error.message : '支払い処理中にエラーが発生しました';
      setErrorMessage(errorMessage);

      toaster.create({
        title: '支払いに失敗しました',
        description: errorMessage,
        type: 'error',
        duration: 5000,
      });

      setCurrentStep('error');
    }
  };

  // 支払いリトライ処理
  const handleRetryPayment = () => {
    setErrorMessage(null);
    setCurrentStep('confirm');
  };

  // 前の画面に戻る処理
  const handleGoBack = () => {
    if (currentStep === 'confirm') {
      // 確認画面から金額入力画面へ戻る（金額情報がない場合のみ）
      if (scannedData?.amount == null) {
        setCurrentStep('amount-input');
        setPaymentAmount(null);
        setSelectedItems([]);
        setSelectedItems([]);
      } else {
        // 金額情報がある場合は、QRスキャンからやり直し
        handleScanClose();
      }
    } else if (currentStep === 'amount-input') {
      // 金額入力画面からQRスキャンへ戻る
      setIsPaymentDrawerOpen(false);
      setCurrentStep('amount-input');
      setTimeout(() => {
        setIsScanDrawerOpen(true);
      }, 300);
    }
  };

  // 戻るボタンが表示可能かどうか
  const canGoBack =
    currentStep === 'amount-input' || (currentStep === 'confirm' && scannedData?.amount == null);

  // 支払い完了画面を閉じる処理
  const handlePaymentDrawerClose = () => {
    handleScanClose();
  };

  // 支払いキャンセル時の処理
  const handlePaymentCancel = () => {
    handleScanClose();
  };

  return {
    isScanDrawerOpen,
    isMyQRDrawerOpen,
    isPaymentDrawerOpen,
    currentStep,
    scannedData,
    paymentAmount,
    transactionId,
    errorMessage,
    canGoBack,
    handleScanClick,
    handleMyQRClick,
    handleScanClose,
    handleQRScanned,
    handleQRScan,
    handleQRCodeFromUrl,
    handleResetScanned,
    handleAmountSubmit,
    handlePaymentConfirm,
    handlePaymentCancel,
    handlePaymentDrawerClose,
    handleRetryPayment,
    handleGoBack,
    setIsScanDrawerOpen,
    setIsMyQRDrawerOpen,
  };
};
